// src/shared/auth/tenantContext.tsx
// ────────────────────────────────────────────────────────────────────────────
// Tenant Override Context — GCP IAM–inspired impersonation for superadmins.
//
// When a superadmin selects a tenant in the TenantSelector dropdown, this
// context stores the override and injects an `X-Tenant-Override` header on
// every BFF request via the bffClient auth-provider hook.
//
// Non-superadmin users see no tenant selector and no override is applied.
// ────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './session';
import { bff } from '@/lib/http/bff';
import { listTenantsForPicker, type TenantPickerEntry } from '@/features/system/iam-admin/api/iamAdminApi';
import { useBrowserPathname } from '@/shared/navigation/useBrowserPathname';

/* ── Context shape ──────────────────────────────────────────────────── */
interface TenantOverrideCtx {
  /** True when the logged-in user has superadmin privilege. */
  isSuperadmin: boolean;
  /** All available tenants (fetched once for superadmins). */
  tenants: TenantPickerEntry[];
  /** Currently active tenant id (override or own). */
  activeTenantId: number | null;
  /** The user's own tenant_id from their session (never changes). */
  ownTenantId: number | null;
  /** Whether the user is currently impersonating a different tenant. */
  isOverriding: boolean;
  /** Set the active tenant override. Pass `null` to revert to own tenant. */
  setOverride: (tenantId: number | null) => void;
  /** Loading state for the tenant list. */
  loading: boolean;
}

const Ctx = createContext<TenantOverrideCtx | null>(null);

const STORAGE_KEY = 'pacmaster:tenantOverride';

function readPersistedOverride(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function persistOverride(id: number | null) {
  try {
    if (id == null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, String(id));
  } catch { /* SSR-safe */ }
}

/* ── Provider ───────────────────────────────────────────────────────── */
export function TenantProvider({ children }: { children: React.ReactNode }) {
  const pathname = useBrowserPathname();
  const { user, capabilities, loading: authLoading, refresh } = useAuth();
  const queryClient = useQueryClient();
  const isSharedPublicPath = pathname.startsWith('/s/');

  const isSuperadmin = capabilities.isSuperadmin === true;
  const ownTenantId: number | null = (() => {
    const candidates = [
      user?.business_tenant_id,
      user?.businessTenantId,
      user?.business_tenant?.idTenant,
      user?.business_tenant?.tenant_id,
      user?.business_tenant?.id_tenant,
      user?.business_tenant?.id,
      user?.tenant_id,
      user?.tenantId,
      user?.tenant?.idTenant,
      user?.tenant?.tenant_id,
      user?.tenant?.id_tenant,
      user?.tenant?.id,
      user?.id_tenant,
      capabilities?.tenant_id,
      capabilities?.tenantId,
      capabilities?.id_tenant,
    ];

    for (const raw of candidates) {
      if (raw == null) continue;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }

    return null;
  })();

  const [tenants, setTenants] = useState<TenantPickerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [overrideTenantId, setOverrideTenantId] = useState<number | null>(readPersistedOverride);

  // Ref kept in sync during render — readable by the auth provider callback
  // without waiting for an effect (avoids child-before-parent effect ordering issues).
  const overrideRef = useRef(overrideTenantId);
  overrideRef.current = overrideTenantId;

  // Bootstrap guard: when a persisted override exists, optimistically apply it
  // while auth/capabilities are still loading so first-mount data fetches do
  // not burst against the home tenant and then flip once superadmin resolves.
  const hasPersistedOverride = overrideTenantId != null;
  const allowBootstrapOverride = authLoading && hasPersistedOverride;
  const overrideEligible = isSuperadmin || allowBootstrapOverride;

  // Track whether we've already fetched the tenant list for this session
  const fetchedRef = useRef(false);
  const fetchedMineRef = useRef(false);
  const tenantHydrationRefreshRef = useRef(false);

  // Fetch tenant list once for superadmins
  useEffect(() => {
    if (isSharedPublicPath) return;
    if (authLoading || !isSuperadmin || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);

    listTenantsForPicker()
      .then(async (list) => {
        const sorted = (Array.isArray(list) ? list : []).sort(
          (a, b) => (a.idTenant ?? 0) - (b.idTenant ?? 0),
        );
        setTenants(sorted);

        setOverrideTenantId((current) => {
          if (current != null && current !== 1) return current;
          if (ownTenantId != null && ownTenantId !== 1) return current;
          if (current != null && current === 1) {
            persistOverride(null);
          }
          return current;
        });
      })
      .catch(() => { /* non-fatal — selector just won't appear */ })
      .finally(() => setLoading(false));
  }, [authLoading, isSharedPublicPath, isSuperadmin, ownTenantId]);

  // For non-superadmin sessions, hydrate current tenant label from the session
  // snapshot that /auth/me already materializes on the BFF side.
  useEffect(() => {
    if (isSharedPublicPath) return;
    if (authLoading || isSuperadmin || fetchedMineRef.current) return;

    const tenantName = String((user as any)?.tenant_name ?? (user as any)?.tenantName ?? '').trim();
    if (!ownTenantId || ownTenantId === 1 || !tenantName) return;

    fetchedMineRef.current = true;

    setTenants([
      {
        idTenant: ownTenantId,
        tenantRef: String(ownTenantId),
        tenantName,
        isActive: true,
      },
    ]);
  }, [authLoading, isSharedPublicPath, isSuperadmin, ownTenantId, user]);

  // OIDC mobile propagation guard: when /auth/me lands without tenant fields
  // on first render, force one immediate refresh so tenant-scoped panels can
  // materialize without requiring a manual hard reload.
  useEffect(() => {
    if (isSharedPublicPath) return;
    if (authLoading) return;

    if (ownTenantId != null) {
      tenantHydrationRefreshRef.current = false;
      return;
    }

    if (!user) return;
    if (tenantHydrationRefreshRef.current) return;
    tenantHydrationRefreshRef.current = true;

    void refresh(true).catch(() => {
      // non-fatal: existing bridge/SSE paths continue to retry naturally
    });
  }, [authLoading, isSharedPublicPath, ownTenantId, refresh, user]);

  // Active tenant: override if set, else own
  const activeTenantId = overrideEligible && overrideTenantId != null
    ? overrideTenantId
    : ownTenantId;
  const isOverriding = isSuperadmin && overrideTenantId != null && overrideTenantId !== ownTenantId;

  // Set override + persist
  const setOverride = useCallback((id: number | null) => {
    setOverrideTenantId(id);
    persistOverride(id);
  }, []);

  // ── Inject X-Tenant-Override header into all BFF requests ──────────
  // CRITICAL: Install the auth provider **synchronously during render**, NOT
  // in a useEffect.  React fires child effects before parent effects, so a
  // useEffect here would run AFTER child hooks (useLiveCatalog, etc.) have
  // already fired their first fetch — causing the first request burst to go
  // out without X-Tenant-Override, minting JWTs with the caller's own
  // tenant_id instead of the override.
  //
  // By setting the provider during render we guarantee it's installed before
  // any child useEffect can fire.  The callback reads from overrideRef which
  // is also updated during render, so it always returns the latest value.
  const prevSuperadminRef = useRef<boolean | null>(null);
  const shouldInstallOverrideProvider = overrideEligible && !isSharedPublicPath;
  if (prevSuperadminRef.current !== shouldInstallOverrideProvider) {
    prevSuperadminRef.current = shouldInstallOverrideProvider;
    if (!shouldInstallOverrideProvider) {
      bff.setAuthProvider(() => null);
    } else {
      bff.setAuthProvider(() => {
        const ov = overrideRef.current;
        if (ov != null && ov > 0) {
          return { 'X-Tenant-Override': String(ov) };
        }
        return null;
      });
    }
  }

  // Hygiene: if a non-superadmin logs in and a stale override was persisted,
  // clear it so future sessions don't send pointless denied override headers.
  useEffect(() => {
    if (isSharedPublicPath) return;
    if (authLoading) return;
    if (isSuperadmin) return;
    if (overrideTenantId == null) return;
    setOverrideTenantId(null);
    persistOverride(null);
    overrideRef.current = null;
  }, [authLoading, isSharedPublicPath, isSuperadmin, overrideTenantId]);

  // ── Invalidate all cached queries when the active tenant changes ────
  // This forces every panel/table/list to refetch with the new
  // X-Tenant-Override header so content updates immediately on switch.
  const prevTenantRef = useRef(activeTenantId);
  useEffect(() => {
    if (prevTenantRef.current !== activeTenantId) {
      prevTenantRef.current = activeTenantId;
      // Small delay so the header provider is installed first
      queueMicrotask(() => queryClient.invalidateQueries());
    }
  }, [activeTenantId, queryClient]);

  const value: TenantOverrideCtx = {
    isSuperadmin,
    tenants,
    activeTenantId,
    ownTenantId,
    isOverriding,
    setOverride,
    loading,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/* ── Hook ───────────────────────────────────────────────────────────── */
export function useTenantOverride() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTenantOverride must be used inside <TenantProvider>');
  return ctx;
}
